import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {convertStrandToDisplayName, Strand, StrandOfDoubleStranded} from 'constants/MolbioConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARKER} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

const ORDERED_STRAND_DROPDOWN_VALUES = [1, -1, 0];
const ORDERED_STRAND_DROPDOWN_VALUES_WITHOUT_NONE = [1, -1];
const NO_CHANGE_TEXT = 'Keep existing strands';

interface BaseStrandDropdownProps {
  className?: string;
  disabled?: boolean;
  isMenuOpen?: boolean;
  onToggleMenuOpen?: (isOpen: boolean) => void;
  allowNone?: boolean;
  includeNullOption?: boolean;
}

interface StrandOfDoubleStrandedDropdownProps {
  strand: StrandOfDoubleStranded | null;
  onSelectStrand: (value: StrandOfDoubleStranded | null) => void;
  allowNone: false;
}

interface StrandOrNoneDropdownProps {
  strand: Strand | null;
  onSelectStrand: (value: Strand | null) => void;
  allowNone: true;
}

type StrandDropdownProps = BaseStrandDropdownProps &
  (StrandOfDoubleStrandedDropdownProps | StrandOrNoneDropdownProps);

/**
 * Dropdown component for selecting a strand for sequence features (annotations, translations)
 */
function StrandDropdown({
  className,
  strand,
  onSelectStrand,
  disabled,
  isMenuOpen,
  onToggleMenuOpen,
  allowNone = true,
  includeNullOption = false,
}: StrandDropdownProps): JSX.Element {
  return (
    <div className={className} {...getTestProps({component: 'StrandDropdown', element: 'root'})}>
      <Dropdown
        alwaysDropDown={true}
        menu={
          <Menu value={strand} width={MenuWidth.FULL}>
            {includeNullOption && (
              <Option onSelect={onSelectStrand} value={null}>
                {NO_CHANGE_TEXT}
              </Option>
            )}
            {(allowNone ? ORDERED_STRAND_DROPDOWN_VALUES : ORDERED_STRAND_DROPDOWN_VALUES_WITHOUT_NONE).map(
              (strand) => (
                <Option onSelect={onSelectStrand} value={strand} key={strand}>
                  {convertStrandToDisplayName(strand as Strand)}
                </Option>
              )
            )}
          </Menu>
        }
        isOpen={isMenuOpen}
        onToggle={onToggleMenuOpen}
      >
        <button
          className={classNames('btn-af-default', 'selectDropdown-button', {'is-disabled': disabled})}
          type="button"
          disabled={disabled}
          {...getTestProps({component: 'StrandDropdown', element: 'input'})}
        >
          <span className="u-lr-split">
            <span className="selectDropdown-text">
              {strand == null ? NO_CHANGE_TEXT : convertStrandToDisplayName(strand)}
            </span>
            <BenchlingIcon className={css(styles.buttonCaret)} icon="chevron-down" />
          </span>
        </button>
      </Dropdown>
    </div>
  );
}

const styles = StyleSheet.create({
  buttonCaret: {
    color: GRAY_DARKER,
    marginLeft: '5px',
  },
});

export default StrandDropdown;
